/**
 * `/demo` — the ephemeral demo surface (#1753).
 *
 * One primary action: mint a per-visitor demo team from the Meridian
 * Emergency scenario (`POST /api/demo-sessions`) and land in its
 * pre-filled cycle workspace. The terms are stated up front, honestly:
 * a pre-filled team, one solve of up to 15 seconds, nothing kept —
 * the team is deleted after two hours.
 *
 * Graceful degradation: the SPA auto-deploys ahead of the backend, so
 * when the mint endpoint isn't live yet (404/405) this page falls back
 * to the persona picker (`DemoPage`), which also remains reachable at
 * `/demo/scenarios` for sales demos.
 */
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { ApiError, apiFetch } from "../api/client";
import { DemoEndedNotice } from "../components/DemoEndedNotice";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui";
import { type DemoSessionState, leaveDemoOnClick, storeDemoSession } from "../lib/demoSession";
import { DemoPage } from "./DemoPage";
import { TariiWord } from "./marketing/TariiWord";
import "./marketing/marketing-brand.css";

/** Response shape of `POST /api/demo-sessions` (mirrors the backend
 * `DemoSessionResponse`; kept structural so an older backend that omits
 * a field degrades to sensible defaults). */
interface DemoSessionMintResponse {
  token: string;
  account_id: string;
  team_id: string;
  cycle_id: string | null;
  team_name: string;
  expires_at: string;
  solve_time_limit_s: number;
  solves_remaining: number;
}

/** The three demo terms, stated before the visitor commits. Terse and
 * honest — the same numbers the server enforces. */
const TERMS: ReadonlyArray<{ label: string; body: string }> = [
  {
    label: "Pre-filled",
    body: "A 28-person emergency department — Meridian ED — with its roster history and current requests already in place.",
  },
  {
    label: "One solve",
    body: "You run the solver once, for up to 15 seconds.",
  },
  {
    label: "Nothing kept",
    body: "Your demo team is yours alone and is deleted after two hours.",
  },
];

export function DemoStartPage() {
  const [minting, setMinting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // The mint endpoint is missing on older backend deploys (the SPA
  // auto-deploys first). 404/405 → render the persona picker instead.
  const [fallbackToPicker, setFallbackToPicker] = useState(false);

  async function startDemo() {
    setMinting(true);
    setErrorMessage(null);
    try {
      const minted = await apiFetch<DemoSessionMintResponse>("/api/demo-sessions", {
        method: "POST",
      });
      const session: DemoSessionState = {
        token: minted.token,
        account_id: minted.account_id,
        team_id: minted.team_id,
        cycle_id: minted.cycle_id,
        team_name: minted.team_name,
        expires_at: minted.expires_at,
        solve_time_limit_s: minted.solve_time_limit_s,
        solves_remaining: minted.solves_remaining,
      };
      storeDemoSession(session);
      // Hard navigation (not router) so `AuthProvider` re-mounts and
      // picks up the demo-session provider before any data loads. Land
      // directly in the pre-filled cycle workspace — the trimmed path.
      window.location.assign(
        session.cycle_id ? `/cycles/${session.cycle_id}/roster` : `/teams/${session.team_id}`,
      );
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 405)) {
        setFallbackToPicker(true);
      } else if (err instanceof ApiError) {
        setErrorMessage(err.detail);
      } else {
        setErrorMessage("Something went wrong starting the demo. Try again in a moment.");
      }
      setMinting(false);
    }
  }

  if (fallbackToPicker) {
    return <DemoPage />;
  }

  return (
    <div data-marketing-brand="" className="flex min-h-screen flex-col bg-bg">
      {/* #2779 — a way back to the website from the demo entry. */}
      <nav className="mx-auto w-full max-w-2xl px-4 pt-6 sm:px-6">
        <a
          href="/"
          onClick={leaveDemoOnClick}
          className="inline-flex min-h-tap items-center gap-1.5 text-sm text-fg-muted underline-offset-4 hover:text-fg hover:underline"
          data-testid="demo-back-to-site"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to the website
        </a>
      </nav>
      <DemoEndedNotice />
      <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-center gap-10 px-4 py-12 sm:px-6">
        <div className="flex flex-col gap-8">
          <a
            href="/"
            onClick={leaveDemoOnClick}
            aria-label="Back to the Tarii home page"
            className="self-start"
          >
            <Logo className="h-10" />
          </a>
          <header className="flex flex-col gap-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-fg-subtle">
              Product demo
            </p>
            <h1 className="text-2xl font-semibold tracking-tight text-fg">
              Try <TariiWord /> on a working department.
            </h1>
            <p className="max-w-prose text-sm leading-relaxed text-fg-muted">
              We set up a temporary team for you — nothing to install, no account needed. Look
              around, then run the one solve and watch the roster come together.
            </p>
          </header>
        </div>

        <dl className="divide-y divide-border border-y border-border" data-testid="demo-terms">
          {TERMS.map((term) => (
            <div key={term.label} className="grid grid-cols-[7.5rem_1fr] gap-4 py-3">
              <dt className="font-mono text-[11px] uppercase tracking-[0.18em] text-fg-subtle">
                {term.label}
              </dt>
              <dd className="text-sm leading-relaxed text-fg-muted">{term.body}</dd>
            </div>
          ))}
        </dl>

        <div className="flex flex-col gap-4">
          <Button
            size="lg"
            className="self-start"
            onClick={() => void startDemo()}
            disabled={minting}
            data-testid="demo-start-button"
          >
            {minting ? "Setting up your team…" : "Start the demo"}
          </Button>
          {errorMessage ? (
            <p className="text-sm text-fg-muted" role="alert" data-testid="demo-start-error">
              {errorMessage}
            </p>
          ) : null}
          <p className="text-sm text-fg-subtle">
            Prefer a guided tour of prepared examples?{" "}
            <Link to="/demo/scenarios" className="underline underline-offset-2 hover:text-fg">
              Browse the scenarios
            </Link>
            .
          </p>
        </div>
      </main>
    </div>
  );
}
